import React from 'react'
import { createContext } from 'src/util'
import useHandler from 'src/hooks/useHandler'
import { useSortedData } from 'src/hooks'

const defaultValue = {}

/**
 * Filter a data array respecting all given filters
 */
const useFilteredData = ({ data = [], filters }) =>
  React.useMemo(() => data.filter(item => Object.keys(filters).every(key => filters[key].filter(item))), [
    filters,
    data,
  ])

/**
 * Creates a context for filtering and sorting table data
 */
export default createContext(
  Provider =>
    function TableContext(props) {
      const [filters, setFilters] = React.useState({})

      // create hook used to register and unregister a filter for this table
      const useFilter = useHandler(function useFilter({ name, ...config }, deps) {
        React.useEffect(() => {
          setFilters(state => ({ ...state, [name]: config }))
          return () => setFilters(({ [name]: config, ...state }) => state)
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, deps)
      })

      // create current sorting state
      const [{ sortBy, sortDirection, sortWith }, sort] = React.useState({})

      // create hook to generate and memoize filtered and sorted data
      const useData = useHandler(data =>
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useSortedData({ sortBy, sortDirection, sortWith, data: useFilteredData({ data, filters }) })
      )

      return (
        <Provider
          {...props}
          value={{
            filters,
            sort,
            sortBy,
            sortDirection,
            useData,
            useFilter,
          }}
        />
      )
    },
  defaultValue
)
